const supabaseUrl = (import.meta.env.VITE_SUPABASE_URL ?? "").replace(/\/+$/, "");
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY ?? "";
const SESSION_STORAGE_KEY = "fispro-os:supabase-session:v1";
const PASSWORD_RECOVERY_STORAGE_KEY = "fispro-os:supabase-password-recovery:v1";

export const isSupabaseConfigured = Boolean(supabaseUrl && supabaseAnonKey);

export type SupabaseUser = {
  id: string;
  email?: string;
};

export type SupabaseSession = {
  accessToken: string;
  refreshToken: string;
  expiresAt: number;
  user: SupabaseUser;
};

export type SupabaseAuthInitialization = {
  session: SupabaseSession | null;
  passwordRecovery: boolean;
};

export type SupabasePushSubscription = {
  endpoint: string;
  p256dh: string;
  auth: string;
  userAgent?: string;
};

export type SupabasePushDeviceStatus = {
  registered: boolean;
  lastSuccessfulPushAt?: string;
  lastFailureAt?: string;
  lastErrorCategory?: string;
};

export type MeetingDebriefAnalysisResponse = {
  analysis: DebriefAnalysis;
  transcript?: string;
  requestId?: string;
};

export type MeetingDebriefAnalysisInput = {
  caseId: string;
  client: string;
  property: string;
  eventType: string;
  notes?: string;
  referenceTimestamp: string;
  timeZone: "Europe/Bratislava";
  playbook: unknown;
};

type AuthTokenResponse = {
  access_token: string;
  refresh_token: string;
  expires_in?: number;
  expires_at?: number;
  user?: {
    id?: string;
    email?: string;
  };
};

export class SupabaseRequestError extends Error {
  status: number;
  category?: DebriefErrorCategory;
  retryable?: boolean;
  requestId?: string;

  constructor(message: string, status: number, details?: { category?: DebriefErrorCategory; retryable?: boolean; requestId?: string }) {
    super(message);
    this.name = "SupabaseRequestError";
    this.status = status;
    this.category = details?.category;
    this.retryable = details?.retryable;
    this.requestId = details?.requestId;
  }
}

const requestJson = async <T>(path: string, init: RequestInit = {}, accessToken?: string): Promise<T> => {
  if (!isSupabaseConfigured) {
    throw new Error("Supabase nie je nakonfigurovaný.");
  }

  const headers = new Headers(init.headers);
  headers.set("apikey", supabaseAnonKey);
  if (accessToken) {
    headers.set("Authorization", `Bearer ${accessToken}`);
  }
  if (init.body && !(init.body instanceof FormData) && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }

  const response = await fetch(`${supabaseUrl}${path}`, { ...init, headers });
  if (!response.ok) {
    const payload = await response.json().catch(() => null) as {
      message?: string; msg?: string; error?: string; error_description?: string;
      category?: string; retryable?: boolean; requestId?: string;
    } | null;
    const category = DEBRIEF_ERROR_CATEGORIES.includes(payload?.category as DebriefErrorCategory)
      ? payload?.category as DebriefErrorCategory
      : undefined;
    throw new SupabaseRequestError(
      payload?.message ?? payload?.msg ?? payload?.error ?? payload?.error_description ?? `Supabase request zlyhal (${response.status}).`,
      response.status,
      { category, retryable: payload?.retryable, requestId: payload?.requestId }
    );
  }

  if (response.status === 204) {
    return undefined as T;
  }

  const text = await response.text();
  return (text ? JSON.parse(text) : undefined) as T;
};

const decodeJwtUser = (accessToken: string): SupabaseUser | null => {
  try {
    const encodedPayload = accessToken.split(".")[1];
    const normalized = encodedPayload.replace(/-/g, "+").replace(/_/g, "/");
    const decoded = JSON.parse(atob(normalized.padEnd(Math.ceil(normalized.length / 4) * 4, "="))) as {
      sub?: string;
      email?: string;
    };

    return decoded.sub ? { id: decoded.sub, email: decoded.email } : null;
  } catch {
    return null;
  }
};

const sessionFromTokenResponse = (payload: AuthTokenResponse): SupabaseSession => {
  const user = payload.user?.id
    ? { id: payload.user.id, email: payload.user.email }
    : decodeJwtUser(payload.access_token);

  if (!user) {
    throw new Error("Prihlásená Supabase session neobsahuje používateľa.");
  }

  return {
    accessToken: payload.access_token,
    refreshToken: payload.refresh_token,
    expiresAt: payload.expires_at ?? Math.floor(Date.now() / 1000) + (payload.expires_in ?? 3600),
    user
  };
};

const saveSession = (session: SupabaseSession) => {
  window.localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(session));
};

const clearSession = () => {
  window.localStorage.removeItem(SESSION_STORAGE_KEY);
};

const setPasswordRecoveryPending = () => {
  window.sessionStorage.setItem(PASSWORD_RECOVERY_STORAGE_KEY, "true");
};

const isPasswordRecoveryPending = () =>
  window.sessionStorage.getItem(PASSWORD_RECOVERY_STORAGE_KEY) === "true";

export const completeSupabasePasswordRecovery = () => {
  window.sessionStorage.removeItem(PASSWORD_RECOVERY_STORAGE_KEY);
};

const loadStoredSession = (): SupabaseSession | null => {
  try {
    const stored = window.localStorage.getItem(SESSION_STORAGE_KEY);
    if (!stored) {
      return null;
    }

    const session = JSON.parse(stored) as Partial<SupabaseSession>;
    if (
      typeof session.accessToken !== "string" ||
      typeof session.refreshToken !== "string" ||
      typeof session.expiresAt !== "number" ||
      !session.user ||
      typeof session.user.id !== "string"
    ) {
      return null;
    }

    return session as SupabaseSession;
  } catch {
    return null;
  }
};

const authRedirectParameterNames = [
  "access_token",
  "refresh_token",
  "expires_at",
  "expires_in",
  "token_type",
  "type",
  "error",
  "error_code",
  "error_description"
] as const;

const cleanAuthParametersFromUrl = () => {
  const url = new URL(window.location.href);
  const searchParams = new URLSearchParams(url.search);
  const hashParams = new URLSearchParams(url.hash.replace(/^#/, ""));

  authRedirectParameterNames.forEach((name) => {
    searchParams.delete(name);
    hashParams.delete(name);
  });

  const search = searchParams.toString();
  const hash = hashParams.toString();
  window.history.replaceState(
    {},
    document.title,
    `${url.pathname}${search ? `?${search}` : ""}${hash ? `#${hash}` : ""}`
  );
};

const sessionFromRedirect = (): SupabaseAuthInitialization | null => {
  const hashParams = new URLSearchParams(window.location.hash.replace(/^#/, ""));
  const searchParams = new URLSearchParams(window.location.search);
  const readParameter = (name: string) => hashParams.get(name) ?? searchParams.get(name);
  const accessToken = readParameter("access_token");
  const refreshToken = readParameter("refresh_token");
  const authType = readParameter("type")?.toLowerCase();
  const passwordRecovery = authType === "recovery" || authType === "password_recovery";
  const hasAuthRedirectParameters = Boolean(
    accessToken ||
      refreshToken ||
      passwordRecovery ||
      readParameter("error") ||
      readParameter("error_code")
  );

  if (!hasAuthRedirectParameters) {
    return null;
  }

  try {
    if (!accessToken || !refreshToken) {
      return { session: null, passwordRecovery: false };
    }

    const session = sessionFromTokenResponse({
      access_token: accessToken,
      refresh_token: refreshToken,
      expires_at: Number(readParameter("expires_at")) || undefined,
      expires_in: Number(readParameter("expires_in")) || 3600
    });

    saveSession(session);
    if (passwordRecovery) {
      setPasswordRecoveryPending();
    } else {
      completeSupabasePasswordRecovery();
    }

    return { session, passwordRecovery };
  } finally {
    cleanAuthParametersFromUrl();
  }
};

export const refreshSupabaseSession = async (session: SupabaseSession): Promise<SupabaseSession> => {
  const payload = await requestJson<AuthTokenResponse>("/auth/v1/token?grant_type=refresh_token", {
    method: "POST",
    body: JSON.stringify({ refresh_token: session.refreshToken })
  });
  const refreshedSession = sessionFromTokenResponse(payload);
  saveSession(refreshedSession);
  return refreshedSession;
};

export const ensureValidSupabaseSession = async (session: SupabaseSession): Promise<SupabaseSession> => {
  if (session.expiresAt - Math.floor(Date.now() / 1000) > 60) {
    return session;
  }

  return refreshSupabaseSession(session);
};

export const initializeSupabaseSession = async (): Promise<SupabaseAuthInitialization> => {
  if (!isSupabaseConfigured) {
    return { session: null, passwordRecovery: false };
  }

  const redirectInitialization = sessionFromRedirect();
  if (redirectInitialization) {
    return redirectInitialization;
  }

  const storedSession = loadStoredSession();
  if (!storedSession) {
    completeSupabasePasswordRecovery();
    return { session: null, passwordRecovery: false };
  }

  try {
    return {
      session: await ensureValidSupabaseSession(storedSession),
      passwordRecovery: isPasswordRecoveryPending()
    };
  } catch (error) {
    if (error instanceof SupabaseRequestError && (error.status === 400 || error.status === 401)) {
      clearSession();
      completeSupabasePasswordRecovery();
      return { session: null, passwordRecovery: false };
    }

    return { session: storedSession, passwordRecovery: isPasswordRecoveryPending() };
  }
};

export const signInWithSupabasePassword = async (email: string, password: string): Promise<SupabaseSession> => {
  const payload = await requestJson<AuthTokenResponse>("/auth/v1/token?grant_type=password", {
    method: "POST",
    body: JSON.stringify({ email, password })
  });
  const session = sessionFromTokenResponse(payload);
  completeSupabasePasswordRecovery();
  saveSession(session);
  return session;
};

export const updateSupabasePassword = async (
  session: SupabaseSession,
  password: string
): Promise<SupabaseSession> => {
  const validSession = await ensureValidSupabaseSession(session);
  const user = await requestJson<SupabaseUser>(
    "/auth/v1/user",
    {
      method: "PUT",
      body: JSON.stringify({ password })
    },
    validSession.accessToken
  );
  const updatedSession = {
    ...validSession,
    user: user.id ? { id: user.id, email: user.email } : validSession.user
  };
  completeSupabasePasswordRecovery();
  saveSession(updatedSession);
  return updatedSession;
};

export const signOutFromSupabase = async (session: SupabaseSession) => {
  try {
    await requestJson<void>("/auth/v1/logout", { method: "POST" }, session.accessToken);
  } finally {
    clearSession();
    completeSupabasePasswordRecovery();
  }
};

export const supabaseRequest = async <T>(
  path: string,
  session: SupabaseSession,
  init: RequestInit = {}
): Promise<{ data: T; session: SupabaseSession }> => {
  const validSession = await ensureValidSupabaseSession(session);
  const data = await requestJson<T>(path, init, validSession.accessToken);
  return { data, session: validSession };
};

export const saveSupabasePushSubscription = async (
  subscription: SupabasePushSubscription,
  session: SupabaseSession
): Promise<SupabaseSession> => {
  const now = new Date().toISOString();
  const result = await supabaseRequest<void>("/rest/v1/push_subscriptions?on_conflict=endpoint", session, {
    method: "POST",
    headers: {
      Prefer: "resolution=merge-duplicates,return=minimal"
    },
    body: JSON.stringify({
      user_id: session.user.id,
      endpoint: subscription.endpoint,
      p256dh: subscription.p256dh,
      auth: subscription.auth,
      user_agent: subscription.userAgent,
      disabled_at: null,
      expired_at: null,
      updated_at: now,
      last_seen_at: now
    })
  });

  return result.session;
};

export const deleteSupabasePushSubscription = async (
  endpoint: string,
  session: SupabaseSession
): Promise<SupabaseSession> => {
  const result = await supabaseRequest<void>(
    `/rest/v1/push_subscriptions?user_id=eq.${encodeURIComponent(session.user.id)}&endpoint=eq.${encodeURIComponent(endpoint)}`,
    session,
    {
      method: "PATCH",
      headers: {
        Prefer: "return=minimal"
      },
      body: JSON.stringify({ disabled_at: new Date().toISOString(), updated_at: new Date().toISOString() })
    }
  );

  return result.session;
};

type PushSubscriptionRow = {
  last_successful_push_at: string | null;
  last_failure_at: string | null;
  last_error_category: string | null;
  disabled_at: string | null;
  expired_at: string | null;
};

export const getSupabasePushSubscriptionStatus = async (
  endpoint: string,
  session: SupabaseSession
): Promise<{ status: SupabasePushDeviceStatus; session: SupabaseSession }> => {
  const result = await supabaseRequest<PushSubscriptionRow[]>(
    `/rest/v1/push_subscriptions?select=last_successful_push_at,last_failure_at,last_error_category,disabled_at,expired_at&user_id=eq.${encodeURIComponent(session.user.id)}&endpoint=eq.${encodeURIComponent(endpoint)}&limit=1`,
    session
  );
  const row = result.data[0];
  return {
    session: result.session,
    status: {
      registered: Boolean(row && !row.disabled_at && !row.expired_at),
      lastSuccessfulPushAt: row?.last_successful_push_at ?? undefined,
      lastFailureAt: row?.last_failure_at ?? undefined,
      lastErrorCategory: row?.last_error_category ?? undefined
    }
  };
};

export const requestSupabaseTestPush = async (session: SupabaseSession) => {
  const result = await supabaseRequest<string>("/rest/v1/rpc/request_test_push", session, {
    method: "POST",
    body: "{}"
  });
  return { requestId: result.data, session: result.session };
};

export const analyzeMeetingDebrief = async (
  session: SupabaseSession,
  input: MeetingDebriefAnalysisInput & { notes: string }
) => {
  const result = await supabaseRequest<unknown>(
    "/functions/v1/analyze-meeting-debrief",
    session,
    { method: "POST", body: JSON.stringify(input) }
  );
  const response = result.data as { analysis?: unknown; transcript?: unknown; requestId?: unknown };
  return {
    analysis: parseDebriefAnalysis(response?.analysis ?? result.data),
    transcript: typeof response?.transcript === "string" ? response.transcript : undefined,
    requestId: typeof response?.requestId === "string" ? response.requestId : undefined,
    session: result.session
  };
};

export const analyzeMeetingDebriefAudio = async (
  session: SupabaseSession,
  input: MeetingDebriefAnalysisInput,
  audio: Blob,
  durationSeconds: number
) => {
  const form = new FormData();
  const extension = audio.type.includes("mp4") ? "m4a" : audio.type.includes("ogg") ? "ogg" : "webm";
  form.set("audio", audio, `debrief.${extension}`);
  form.set("durationSeconds", String(durationSeconds));
  form.set("context", JSON.stringify(input));
  const result = await supabaseRequest<unknown>(
    "/functions/v1/analyze-meeting-debrief",
    session,
    { method: "POST", body: form }
  );
  const response = result.data as { analysis?: unknown; transcript?: unknown; requestId?: unknown };
  if (typeof response?.transcript !== "string" || !response.transcript.trim()) {
    throw new SupabaseRequestError("Prepis nahrávky neobsahuje text.", 502, { category: "TRANSCRIPTION_ERROR", retryable: true });
  }
  return {
    analysis: parseDebriefAnalysis(response.analysis),
    transcript: response.transcript.trim(),
    requestId: typeof response.requestId === "string" ? response.requestId : undefined,
    session: result.session
  };
};
import { DEBRIEF_ERROR_CATEGORIES, parseDebriefAnalysis, type DebriefErrorCategory } from "../debriefIntelligence";
import type { DebriefAnalysis } from "../types";
